import GSApi from 'api/Api';
import { StreamIDKey, StreamUseCase } from 'types/ApiTypes';
import { LivePlayerAssetInfo, PlayerEvents, PlayingAsset, PlayingAssetType, PlayingMode, VodPlayerAssetInfo } from 'types/Player';
import {
    isCatchupPlayerAsset,
    isLivePlayerAsset,
    isRecordingPlayerAsset,
    isTrailerPlayerAsset,
    isVodPlayerAsset,
} from 'utils/fnTypeGuards';
import { MILLISECONDS, MINUTES, SECONDS } from 'utils/TimeUnit';
import ApplicationConfig from 'providers/useConfig/ApplicationConfig';
import { isNow, isPast } from 'utils/fnDate';
import { fetchCastTokens } from 'providers/useAuth/AuthService';
import { castAssetType } from 'utils/fnPlayerUI';
import CastHandler from 'providers/cast/CastHandler';
import { isSafari } from 'utils/fnDevices';
import { getBroadcastStreamUseCase, isAssetWithinPaddingWindow } from 'utils/fnData';
import { isSessionListingForbidden } from 'utils/fnError';
import { CastAssetType } from 'types/Cast';
import { guardedLocalStorage } from 'utils/fnStorage';
import { IPlayer } from './abstract/IPlayer';
import YouboraTracking from './YouboraTracking';
import { STREAM_LIMIT_REACHED, STREAM_SESSION_HEARTBEAT, STREAM_SESSION_ID } from './playerConstants';

class PlaybackManager {
    private playingAsset: PlayingAsset = null;

    private playerEngine: IPlayer = null;

    private playbackMode: PlayingMode = PlayingMode.NORMAL;

    private isPlaying = false;

    private playSession: string | null = null;

    private keepAliveInterval: null | number = null;

    private heartbeatSessionId: string | null = null;

    private heartbeatIntervalMs: number | null = null;

    private lastHeartbeatAt: number | null = null;

    private heartbeatInFlight: boolean = false;

    private abandonedSessions: Set<string> = new Set();

    private sessionExpired: boolean = false;

    private restartingSession: boolean = false;

    private seekTime: number;

    private pauseLiveTime: number;

    private pauseLiveLastPlayTime: number;

    private pauseLiveTimePlayed: number;

    private castConnected: boolean = false;

    private switchingMode: boolean = false;

    private switchTargetSeek: number | null = null;

    private pendingSeek: number | null = null;

    private timeShiftStarted: boolean = false;

    private timeUpdateReceived: boolean = false;

    private bufferEndReceived: boolean = false;

    private seekAppliedValidity: number = Infinity;

    private pauseOnLive: boolean = false;

    private isReplayCapable: boolean = false;

    private releaseSeekLockTimeout: number | null = null;

    private verifySeekTimeout: number | null = null;

    private modeSwitchTimeout: number | null = null;

    private debug = false;

    private playbackModeChangeListener?: (mode: PlayingMode) => void;

    private assetChangedListener?: (asset: PlayingAsset) => void;

    private castInitialisedListener?: () => void;

    private takeoverListener?: (state: boolean) => void;

    private sessionRejectedListener?: () => void;

    private readonly LIVE_SEEKMAX_THRESHOLD = 5;

    private readonly HEARTBEAT_TICK_MS = 5000;

    private log = (logType: 'log' | 'error' | 'info' | 'warn', ...args: any[]): void => {
        if (this.debug) {
            console[logType](`[PlaybackManager]`, ...args);
        }
    };

    private timeUpdatedListener = () => {
        this.timeUpdateReceived = true;
        this.onSeekApplied();
    };

    private bufferingListener = (e) => {
        if (e.buffering === false) {
            this.bufferEndReceived = true;
            this.onSeekApplied();
        }
    };

    /**
     * This logic is used to unfreeze the progress value after the playback is
     * initialised but also to avoid the jumping seekbar
     *
     */
    private onSeekApplied = () => {
        if (this.timeUpdateReceived && Date.now() > this.seekAppliedValidity) {
            if (this.castConnected) {
                const mediaInfo = CastHandler.getMediaInfo();

                if (mediaInfo.loaded === true && mediaInfo.mode === this.playbackMode && mediaInfo.playing) {
                    this.switchingMode = false;
                }
            }
            if (this.seekTime != null) {
                if (!this.switchingMode && this.playbackMode === PlayingMode.TIME_SHIFT) {
                    if (this.castConnected) {
                        const reported = this.playerEngine.getCurrentTime();
                        const caughtUp = reported > 0 && Math.abs(reported - this.seekTime) <= this.LIVE_SEEKMAX_THRESHOLD;
                        if (!caughtUp) {
                            return;
                        }
                    }

                    this.timeShiftStarted = true;
                    this.seekTime = null;
                    this.bufferEndReceived = false;
                    this.timeUpdateReceived = false;
                    this.seekAppliedValidity = Infinity;
                }

                if (!this.switchingMode && this.playbackMode === PlayingMode.NORMAL) {
                    this.seekTime = null;
                    this.bufferEndReceived = false;
                    this.timeUpdateReceived = false;
                    this.seekAppliedValidity = Infinity;
                }
            }
        }
    };

    private awaitCastModeReady = async (mode: PlayingMode, timeoutMs = 8000) => {
        const start = Date.now();
        while (Date.now() - start <= timeoutMs) {
            const mediaInfo = CastHandler.getMediaInfo();
            if (mediaInfo.loaded === true && mediaInfo.mode === mode && mediaInfo.playing) {
                return true;
            }
            // eslint-disable-next-line no-await-in-loop
            await new Promise<void>((resolve) => {
                setTimeout(resolve, 100);
            });
        }
        return false;
    };

    private flushPendingSeek = () => {
        const pending = this.pendingSeek;
        this.pendingSeek = null;
        if (pending != null) {
            this.seek(pending);
        }
    };

    private onSeeked = (to: number, waitForSeconds: number) => {
        this.seekAppliedValidity = Date.now() + SECONDS.toMillis(waitForSeconds);
        if (to !== Infinity) {
            this.seekTime = to;
        }

        // in case something goes wrong unlock the seekbar
        this.releaseSeekLockTimeout = window.setTimeout(
            () => {
                this.timeShiftStarted = true;
                this.seekTime = null;
                this.bufferEndReceived = false;
                this.timeUpdateReceived = false;
                this.seekAppliedValidity = Infinity;
                this.releaseSeekLockTimeout = null;
            },
            SECONDS.toMillis(waitForSeconds + 10)
        );
    };

    private beginModeSwitch = () => {
        this.switchingMode = true;

        clearTimeout(this.modeSwitchTimeout);
        this.modeSwitchTimeout = window.setTimeout(() => {
            if (this.switchingMode) {
                this.log('error', 'mode switch did not complete, releasing the seek lock');
                this.endModeSwitch();
            }
        }, SECONDS.toMillis(20));
    };

    private endModeSwitch = () => {
        clearTimeout(this.modeSwitchTimeout);
        this.modeSwitchTimeout = null;

        this.switchingMode = false;
        this.switchTargetSeek = null;
        this.seekTime = null;
        this.timeShiftStarted = false;
    };

    private registerListeners = () => {
        if (this.playerEngine) {
            this.playerEngine.addEventListener(PlayerEvents.TIME_UPDATE, this.timeUpdatedListener);
            this.playerEngine.addEventListener(PlayerEvents.BUFFERING, this.bufferingListener);
        }
    };

    private unregisterListeners = () => {
        if (this.playerEngine) {
            this.playerEngine.removeEventListener(PlayerEvents.TIME_UPDATE, this.timeUpdatedListener);
            this.playerEngine.removeEventListener(PlayerEvents.BUFFERING, this.bufferingListener);
        }
    };

    private resolveSessionParams = (
        mode: PlayingMode,
        overrideId?: string
    ): { useCase: StreamUseCase; idKey: StreamIDKey; id: string } | null => {
        const asset = this.playingAsset;
        if (!asset) return null;

        if (isLivePlayerAsset(asset)) {
            if (mode === PlayingMode.TIME_SHIFT) {
                return {
                    useCase: getBroadcastStreamUseCase(asset.endTime),
                    idKey: StreamIDKey.LISTING_ID,
                    id: overrideId ?? asset.replayContentId,
                };
            }
            return {
                useCase: StreamUseCase.LIVE,
                idKey: StreamIDKey.CHANNEL_ID,
                id: overrideId ?? (asset.channel_id || asset.channelId),
            };
        }

        if (isRecordingPlayerAsset(asset)) {
            return {
                useCase: StreamUseCase.RECORDING,
                idKey: StreamIDKey.RECORDING_ID,
                id: overrideId ?? asset.recording_id ?? asset.listingId,
            };
        }

        if (isCatchupPlayerAsset(asset)) {
            return {
                useCase: getBroadcastStreamUseCase(asset.endTime),
                idKey: StreamIDKey.LISTING_ID,
                id: overrideId ?? asset.listingId,
            };
        }

        if (isTrailerPlayerAsset(asset)) {
            return {
                useCase: StreamUseCase.VOD_TRAILER,
                idKey: StreamIDKey.VOD_ASSET_ID,
                id: overrideId ?? asset.id,
            };
        }

        if (isVodPlayerAsset(asset)) {
            const vodAsset = asset as VodPlayerAssetInfo;
            return {
                useCase: StreamUseCase.VOD,
                idKey: StreamIDKey.VOD_ASSET_ID,
                id: overrideId ?? vodAsset.titleId ?? vodAsset.id,
            };
        }

        return null;
    };

    private createPlaySession = (mode: PlayingMode, overrideId?: string) => {
        const params = this.resolveSessionParams(mode, overrideId);
        if (!params) {
            this.log('error', 'createPlaySession: unable to resolve session params for asset', this.playingAsset);
            return Promise.resolve(null);
        }

        this.log('info', 'createPlaySession', params);

        return GSApi.createStreamSession(params.useCase, params.idKey, params.id, {}, (res) => res);
    };

    private endAllActiveSessions = async () => {
        try {
            const { response } = await GSApi.activeStreamSessions((res) => res);
            const sessions = (response as { sessions?: { id: string }[] })?.sessions ?? [];

            if (!sessions.length) {
                return;
            }

            await Promise.all(sessions.filter((s) => s?.id).map((s) => GSApi.endStreamSession(s.id)));
        } catch (e) {
            this.log('error', 'endAllActiveSessions failed', e);
        }
    };

    public startHeartbeat = (sessionId: string, intervalSeconds?: number) => {
        if (sessionId && this.abandonedSessions.has(sessionId)) {
            this.log('info', 'startHeartbeat ignored for an already ended session', sessionId);
            return;
        }

        this.stopHeartbeat();

        if (!sessionId || !intervalSeconds || intervalSeconds <= 0) {
            return;
        }

        this.heartbeatSessionId = sessionId;
        this.heartbeatIntervalMs = SECONDS.toMillis(intervalSeconds);
        this.storeHeartbeatSuccess(sessionId);

        this.keepAliveInterval = window.setInterval(this.heartbeatTick, this.HEARTBEAT_TICK_MS);
    };

    public adoptSession = (sessionId: string, intervalSeconds?: number) => {
        if (!sessionId) {
            return;
        }

        if (this.abandonedSessions.has(sessionId)) {
            this.log('info', 'adoptSession ignored for an already ended session', sessionId);
            return;
        }

        const storedSessionId = guardedLocalStorage.getItem(STREAM_SESSION_ID);

        if (storedSessionId === sessionId) {
            return;
        }

        if (storedSessionId?.length && storedSessionId !== 'undefined') {
            this.abandonedSessions.add(storedSessionId);
            GSApi.endStreamSession(storedSessionId);
        }

        guardedLocalStorage.setItem(STREAM_SESSION_ID, sessionId);
        this.startHeartbeat(sessionId, intervalSeconds);
    };

    private storeHeartbeatSuccess = (sessionId: string) => {
        this.lastHeartbeatAt = Date.now();

        guardedLocalStorage.setItem(STREAM_SESSION_HEARTBEAT, JSON.stringify({ id: sessionId, at: this.lastHeartbeatAt }));
    };

    private lastHeartbeatSuccessAt = (sessionId: string): number => {
        try {
            const stored = JSON.parse(guardedLocalStorage.getItem(STREAM_SESSION_HEARTBEAT));

            if (stored?.id === sessionId && stored?.at > 0) {
                return stored.at;
            }
        } catch {
            return this.lastHeartbeatAt;
        }

        return this.lastHeartbeatAt;
    };

    private heartbeatOverdueAfterMs = () => this.heartbeatIntervalMs + this.HEARTBEAT_TICK_MS * 2;

    private sessionStaleAfterMs = () => {
        const configuredGap = MINUTES.toMillis(ApplicationConfig.app_config?.player_settings?.heartbeat_max_gap_minutes ?? 0);

        return Math.max(configuredGap, this.heartbeatOverdueAfterMs());
    };

    private heartbeatTick = () => {
        if (!this.heartbeatSessionId || !this.heartbeatIntervalMs || this.heartbeatInFlight) {
            return;
        }

        const sessionId = this.heartbeatSessionId;
        const sinceLastHeartbeat = Date.now() - this.lastHeartbeatSuccessAt(sessionId);

        if (sinceLastHeartbeat > this.sessionStaleAfterMs()) {
            this.log('info', `no successful heartbeat for ${MILLISECONDS.toSeconds(sinceLastHeartbeat)}s - the session is gone`);
            this.onSessionExpired();
            return;
        }

        if (sinceLastHeartbeat >= this.heartbeatIntervalMs) {
            this.sendHeartbeat(sessionId, sinceLastHeartbeat);
        }
    };

    private sendHeartbeat = async (sessionId: string, sinceLastHeartbeat: number) => {
        this.heartbeatInFlight = true;

        try {
            const { status } = await GSApi.heartbeatStreamSession(sessionId);

            if (sessionId !== this.heartbeatSessionId) {
                return;
            }

            if (status === 410) {
                if (sinceLastHeartbeat > this.heartbeatOverdueAfterMs()) {
                    this.onSessionExpired();
                } else {
                    this.onSessionTakenOver();
                }

                return;
            }

            if (status && status < 400) {
                this.storeHeartbeatSuccess(sessionId);
            }
        } catch (e) {
            this.log('error', 'heartbeat failed', e);
        } finally {
            this.heartbeatInFlight = false;
        }
    };

    private abandonSession = () => {
        const sessionId = this.heartbeatSessionId ?? this.playSession;

        this.stopHeartbeat();

        if (!sessionId) {
            return;
        }

        this.abandonedSessions.add(sessionId);

        if (sessionId === guardedLocalStorage.getItem(STREAM_SESSION_ID)) {
            guardedLocalStorage.removeItem(STREAM_SESSION_ID);
        }

        if (this.playSession === sessionId) {
            this.playSession = null;
        }
    };

    private onSessionTakenOver = () => {
        this.log('info', 'stream session taken over');

        this.abandonSession();

        this.isPlaying = false;
        if (this.playerEngine) {
            this.playerEngine.pause();
        }

        if (this.takeoverListener) {
            this.takeoverListener(true);
        }
    };

    private onSessionExpired = () => {
        this.log('info', 'stream session expired - keep alive was interrupted');

        this.abandonSession();
        this.sessionExpired = true;

        if (this.isPlaying) {
            this.restartExpiredSession();
        }
    };

    private restartExpiredSession = async (): Promise<boolean> => {
        if (this.restartingSession || this.castConnected || !this.playingAsset || !this.playerEngine) {
            return false;
        }

        this.restartingSession = true;

        try {
            const sessionMode = this.pauseOnLive ? PlayingMode.NORMAL : this.playbackMode;
            const startAtLiveEdge = sessionMode === PlayingMode.NORMAL && isLivePlayerAsset(this.playingAsset);
            const startPosition = startAtLiveEdge ? 0 : this.getProgress();
            const { replayContentId } = this.playingAsset as LivePlayerAssetInfo;
            const sessionAssetId = sessionMode === PlayingMode.TIME_SHIFT && replayContentId ? replayContentId : undefined;

            this.log('info', `restartExpiredSession mode=${sessionMode} from=${startPosition}`);

            const createSessionResponse = await this.createPlaySession(sessionMode, sessionAssetId);
            const response = createSessionResponse?.response as {
                id: string;
                heartbeat_interval: number;
                stream_url: string;
                analytics_npaw?: any;
            };

            if (!response?.id || !response?.stream_url) {
                this.log('error', 'restartExpiredSession rejected', createSessionResponse?.error);

                this.isPlaying = false;
                this.playerEngine.pause();

                if (createSessionResponse?.status === STREAM_LIMIT_REACHED && this.takeoverListener) {
                    this.takeoverListener(true);
                } else if (isSessionListingForbidden(createSessionResponse?.error) && this.sessionRejectedListener) {
                    this.sessionRejectedListener();
                }

                return false;
            }

            this.sessionExpired = false;
            this.playSession = response.id;
            guardedLocalStorage.setItem(STREAM_SESSION_ID, response.id);
            this.startHeartbeat(response.id, response.heartbeat_interval);

            const sessionPatch = {
                ...(isLivePlayerAsset(this.playingAsset) ? { id: response.id } : {}),
                sessionId: response.id,
                heartbeat_interval: response.heartbeat_interval,
                manifestUrl: response.stream_url,
                stream_url: response.stream_url,
                analytics_npaw: response.analytics_npaw,
            };

            const restartedAsset = { ...this.playingAsset, ...sessionPatch } as PlayingAsset;

            this.playingAsset = restartedAsset;

            if (startAtLiveEdge) {
                this.pauseOnLive = false;
                this.pauseLiveTime = null;
                this.pauseLiveTimePlayed = null;
                this.pauseLiveLastPlayTime = null;
                this.seekTime = null;
                this.setPlaybackMode(PlayingMode.NORMAL);
            }

            if (this.assetChangedListener) {
                this.assetChangedListener(restartedAsset);
            }

            const wasPlaying = this.isPlaying;
            const suffixDomain = !isVodPlayerAsset(restartedAsset) && !isTrailerPlayerAsset(restartedAsset) ? 'tv' : 'vod';

            this.playerEngine.setAsset(restartedAsset, startPosition, suffixDomain, () => {
                if (wasPlaying) {
                    this.playerEngine.play();
                }
            });

            return true;
        } finally {
            this.restartingSession = false;
        }
    };

    public stopOnFatalError = () => {
        this.log('info', 'fatal stream error - stopping playback');

        this.stopHeartbeat();
        this.isPlaying = false;

        if (this.playerEngine) {
            this.playerEngine.stopPlayback();
        }
    };

    private stopHeartbeat = () => {
        if (this.keepAliveInterval) {
            clearInterval(this.keepAliveInterval);
            this.keepAliveInterval = null;
        }

        this.heartbeatSessionId = null;
        this.heartbeatIntervalMs = null;
        this.lastHeartbeatAt = null;
        this.heartbeatInFlight = false;
        this.sessionExpired = false;

        guardedLocalStorage.removeItem(STREAM_SESSION_HEARTBEAT);
    };

    private endPlaySession = async (endAllSessions = false) => {
        this.log('info', 'endPlaySession');

        this.stopHeartbeat();

        const session = this.playSession;
        this.playSession = null;

        const streamSessionId = guardedLocalStorage.getItem(STREAM_SESSION_ID);
        if (streamSessionId?.length && streamSessionId !== 'undefined') {
            guardedLocalStorage.removeItem(STREAM_SESSION_ID);
            this.abandonedSessions.add(streamSessionId);
            GSApi.endStreamSession(streamSessionId);
        }

        if (session && session !== streamSessionId) {
            this.abandonedSessions.add(session);
            GSApi.endStreamSession(session);
        }

        if (endAllSessions) {
            await this.endAllActiveSessions();
            return true;
        }

        return !!session || (!!streamSessionId && streamSessionId !== 'undefined');
    };

    private isLiveChannelAsset = (asset: PlayingAsset = this.playingAsset): boolean =>
        !!asset && (isLivePlayerAsset(asset) || (asset as LivePlayerAssetInfo).type === PlayingAssetType.LIVE);

    private toPlaybackTime = (currentTime: number): number => {
        const asset = this.playingAsset as LivePlayerAssetInfo;
        if (!asset?.startTime) {
            return currentTime;
        }

        const startSeconds = MILLISECONDS.toSeconds(asset.startTime);
        if (currentTime > startSeconds / 2) {
            return Math.max(0, currentTime - startSeconds);
        }

        return currentTime;
    };

    // True when the engine reports time as a Unix epoch (example: live/replay manifests) rather than 0-based.
    private isEpochTimeline = (): boolean => {
        const asset = this.playingAsset as LivePlayerAssetInfo;
        if (!asset?.startTime || !this.playerEngine) {
            return false;
        }
        const ct = this.playerEngine.getCurrentTime();
        return Number.isFinite(ct) && ct > MILLISECONDS.toSeconds(asset.startTime) / 2;
    };

    private toSeekTime = (programRelative: number): number => {
        const asset = this.playingAsset as LivePlayerAssetInfo;
        if (this.isEpochTimeline() && asset?.startTime) {
            return MILLISECONDS.toSeconds(asset.startTime) + programRelative;
        }
        return programRelative;
    };

    private sameAssetIsPlaying = (asset: PlayingAsset) => {
        if (!this.playingAsset) return false;

        return (
            this.playingAsset.id === asset.id &&
            this.playingAsset.type === asset.type &&
            this.playingAsset.manifestUrl === asset.manifestUrl
        );
    };

    private getBookmark = () => {
        if (
            this.playingAsset &&
            (isCatchupPlayerAsset(this.playingAsset) ||
                isRecordingPlayerAsset(this.playingAsset) ||
                isLivePlayerAsset(this.playingAsset) ||
                (isVodPlayerAsset(this.playingAsset) && this.playingAsset.id))
        ) {
            const duration =
                isVodPlayerAsset(this.playingAsset) || isCatchupPlayerAsset(this.playingAsset)
                    ? this.playingAsset.duration
                    : MILLISECONDS.toSeconds(this.playingAsset.endTime - this.playingAsset.startTime);

            const progressInPercentage = (this.playingAsset.bookmark * 100) / duration;

            if (progressInPercentage > 0 && progressInPercentage < 98) {
                return this.playingAsset.bookmark;
            }
        }
        return 0;
    };

    private setPlaybackMode = (mode: PlayingMode) => {
        this.playbackMode = mode;
        if (this.playbackModeChangeListener) {
            this.playbackModeChangeListener(mode);
        }
    };

    private seekLiveHandler = async (to: number, mode: PlayingMode) => {
        const seekMax = this.getSeekMax();
        const isLiveSeekMax = seekMax > 0 && seekMax - to <= this.LIVE_SEEKMAX_THRESHOLD;

        this.log('info', `seekLiveHandler: to=${to}, mode=${mode}, seekMax=${seekMax}, atLiveEdge=${isLiveSeekMax}`);

        if (mode === PlayingMode.NORMAL) {
            await this.switchToTimeShift(to);
        } else if (isLiveSeekMax) {
            await this.switchToNormalMode();
        } else if (this.playerEngine) {
            this.onSeeked(to, this.castConnected ? 5 : 3);
            this.playerEngine.seekTo(this.toSeekTime(to));
        }
    };

    private switchSession = async (mode: PlayingMode, id: string, seekPosition?: number) => {
        const targetPosition = seekPosition;

        await this.endPlaySession();

        if (this.castConnected) {
            await this.cast(
                {
                    ...this.playingAsset,
                    id,
                },
                mode === PlayingMode.TIME_SHIFT ? targetPosition : undefined
            );

            if (isLivePlayerAsset(this.playingAsset) || (this.playingAsset as LivePlayerAssetInfo)?.type === PlayingAssetType.LIVE) {
                this.playingAsset = {
                    ...this.playingAsset,
                    use_case: mode === PlayingMode.TIME_SHIFT ? PlayingAssetType.REPLAY : PlayingAssetType.LIVE,
                } as PlayingAsset;

                if (this.assetChangedListener) {
                    this.assetChangedListener(this.playingAsset);
                }
            }
        } else {
            const useCase = mode === PlayingMode.NORMAL ? StreamUseCase.LIVE : StreamUseCase.REPLAY;

            const createSessionResponse = await this.createPlaySession(mode, id);

            if (createSessionResponse?.response) {
                const response = createSessionResponse.response as {
                    id: string;
                    heartbeat_interval: number;
                    stream_url: string;
                    duration?: number;
                    analytics_npaw?: any;
                };

                this.playSession = response.id;
                guardedLocalStorage.setItem(STREAM_SESSION_ID, response.id);
                this.startHeartbeat(response.id, response.heartbeat_interval);

                const manifestUrl = response.stream_url;
                const originalDuration = this.playingAsset?.duration || this.getDuration();
                const originalStartTime = this.playingAsset?.startTime;
                const originalEndTime = this.playingAsset?.endTime;

                const updatedAsset = {
                    ...this.playingAsset,
                    id: response.id,
                    sessionId: response.id,
                    heartbeat_interval: response.heartbeat_interval,
                    manifestUrl,
                    stream_url: manifestUrl,
                    use_case: useCase,
                    duration: originalDuration,
                    startTime: originalStartTime,
                    endTime: originalEndTime,
                    analytics_npaw: response.analytics_npaw,
                };

                this.playingAsset = updatedAsset;

                if (this.assetChangedListener) {
                    this.assetChangedListener(updatedAsset);
                }

                if (this.playerEngine) {
                    const wasPlaying = this.isPlaying;

                    const scheduledDuration = originalDuration || 0;
                    const startPosition =
                        mode === PlayingMode.NORMAL
                            ? 0
                            : Math.min(targetPosition || 0, scheduledDuration > 0 ? scheduledDuration - 1 : targetPosition || 0);

                    if (this.seekTime == null || this.seekTime === targetPosition) {
                        this.seekTime = mode === PlayingMode.NORMAL ? null : targetPosition;
                    }

                    await new Promise<void>((resolve) => {
                        setTimeout(resolve, 0);
                    });

                    this.playerEngine.setAsset(this.playingAsset, startPosition, 'tv', () => {
                        if (mode === PlayingMode.TIME_SHIFT) {
                            const immediateTarget = this.seekTime ?? targetPosition;
                            if (immediateTarget && Math.abs(startPosition - immediateTarget) > 2) {
                                this.playerEngine.seekTo(this.toSeekTime(immediateTarget));
                            }

                            const capturedTarget = immediateTarget;

                            if (this.verifySeekTimeout) {
                                clearTimeout(this.verifySeekTimeout);
                            }

                            this.verifySeekTimeout = window.setTimeout(() => {
                                this.verifySeekTimeout = null;

                                if (!this.playerEngine) {
                                    return;
                                }

                                const actualPosition = this.toPlaybackTime(this.playerEngine.getCurrentTime());
                                const finalTarget = this.seekTime ?? capturedTarget;

                                if (finalTarget && Math.abs(actualPosition - finalTarget) > 2) {
                                    this.playerEngine.seekTo(this.toSeekTime(finalTarget));
                                }

                                this.seekTime = null;
                                this.switchingMode = false;
                                this.switchTargetSeek = null;
                                this.timeShiftStarted = true;
                            }, 1000);
                        } else {
                            this.seekTime = null;
                            this.switchingMode = false;
                            this.switchTargetSeek = null;
                            this.timeShiftStarted = false;
                            this.flushPendingSeek();
                        }

                        if (wasPlaying) {
                            this.playerEngine.play();
                        }
                    });
                } else {
                    this.endModeSwitch();
                }
            } else {
                await this.onSessionRejected(mode, createSessionResponse?.error);
                this.endModeSwitch();
                this.pendingSeek = null;
            }
        }
    };

    private onSessionRejected = async (mode: PlayingMode, error: any) => {
        this.log('error', 'createPlaySession rejected', error);

        this.switchingMode = false;
        this.switchTargetSeek = null;
        this.seekTime = null;
        this.pendingSeek = null;
        this.timeShiftStarted = false;

        const forbidden = isSessionListingForbidden(error);

        if (forbidden && this.sessionRejectedListener) {
            this.sessionRejectedListener();
        }

        if (mode === PlayingMode.TIME_SHIFT) {
            await this.switchToNormalMode();
        }
    };

    public getPrePadding = (): number => {
        if (this.playingAsset && (isCatchupPlayerAsset(this.playingAsset) || isRecordingPlayerAsset(this.playingAsset))) {
            return this.playingAsset.pre_padding ?? 0;
        }

        return 0;
    };

    public getPostPadding = (): number => {
        if (this.playingAsset && (isCatchupPlayerAsset(this.playingAsset) || isRecordingPlayerAsset(this.playingAsset))) {
            return this.playingAsset.post_padding ?? 0;
        }
        return 0;
    };

    public getStartTime = () => {
        const castProgress = CastHandler.getCastProgress();

        if (castProgress !== null) {
            return castProgress;
        }

        if (this.isLiveChannelAsset()) {
            if (this.playbackMode === PlayingMode.TIME_SHIFT && this.seekTime) {
                return this.seekTime;
            }

            const url = new URL(window.location.href);
            const startFromLive = url.searchParams.get('live') === 'true';
            if (startFromLive) {
                return 0;
            }
        }

        const bookmark = this.getBookmark() + this.getPrePadding();
        return bookmark;
    };

    public setEngine = (engine: IPlayer) => {
        if (this.playerEngine) {
            this.log('info', 'engine already set');
            return;
        }

        this.log('info', 'setEngine');
        this.playerEngine = engine;
        this.registerListeners();
    };

    public cast = async (castAsset: PlayingAsset, startPosition?: number) => {
        this.log('info', 'cast');
        try {
            await this.endPlaySession();
            if (this.playerEngine) {
                this.playerEngine.resetPlayer();
            }

            const castTokens = await fetchCastTokens();
            const params = this.resolveSessionParams(this.playbackMode);
            const recordingId = isRecordingPlayerAsset(castAsset) ? castAsset.recording_id : undefined;
            const contentId = isLivePlayerAsset(castAsset)
                ? castAsset.listingId
                : isRecordingPlayerAsset(castAsset)
                  ? castAsset.listingId
                  : (params?.id ?? castAsset.id);

            const mediaInfo = new window.chrome.cast.media.MediaInfo(contentId, isLivePlayerAsset(castAsset) ? 'LIVE' : 'BUFFERED');

            const isLive = isLivePlayerAsset(castAsset);
            const isLiveTimeShift = isLive && this.playbackMode === PlayingMode.TIME_SHIFT;
            const useCase: CastAssetType = isLiveTimeShift ? 'REPLAY' : castAssetType(this.playingAsset);

            if (castAsset) {
                const metadata = new window.chrome.cast.media.GenericMediaMetadata();
                metadata.title = castAsset.title;
                metadata.images = castAsset.image ? [new window.chrome.cast.Image(castAsset.image)] : [];
                mediaInfo.metadata = metadata;

                mediaInfo.customData = {
                    ...{
                        contentId,
                        ...(recordingId ? { recordingId } : {}),
                        accessToken: castTokens.access_token,
                        expiresIn: castTokens.expires_in,
                        cacheKey: guardedLocalStorage.getItem('gstr-cache-key') ?? '',
                        useCase,
                        clientId: ApplicationConfig.app_config.auth.client_id,
                        assetType: castAssetType(this.playingAsset),
                        playMode: this.playbackMode,
                        startPosition: startPosition ?? null,
                    },
                    ...(this.playerEngine?.getSnapshot() ?? {}),
                };

                this.log('info', 'casting media info: ', mediaInfo);
                await CastHandler.loadMedia(new window.chrome.cast.media.LoadRequest(mediaInfo));

                if (this.castInitialisedListener) {
                    this.castInitialisedListener();
                }
            }
        } catch (e) {
            this.log('error', e);
        }
    };

    // This is a small hack: merges late-arriving EPG/listing fields (image, title, replayUntil, …) onto the playing
    // asset without reloading. Without it the seekbar/jump buttons stay disabled and the timeline shows 00:00 (jumping between REPLAY and LIVE really fast).
    // The proper fix: set asset later.
    public syncAssetMetadata = (asset: PlayingAsset) => {
        if (!this.playingAsset) {
            return;
        }

        const incoming = asset as LivePlayerAssetInfo;
        const current = this.playingAsset as LivePlayerAssetInfo;

        const SYNC_FIELDS: (keyof LivePlayerAssetInfo)[] = [
            'startTime',
            'endTime',
            'duration',
            'listingId',
            'replayContentId',
            'replayUntil',
            'recordingUntil',
            'cast',
        ];

        const patch: Partial<LivePlayerAssetInfo> = {};

        if (asset.image !== current.image) patch.image = asset.image ?? current.image;
        if (asset.title !== current.title) patch.title = asset.title ?? current.title;

        SYNC_FIELDS.forEach((field) => {
            if (incoming[field] != null && incoming[field] !== current[field]) {
                (patch[field] as LivePlayerAssetInfo[typeof field]) = incoming[field];
            }
        });

        if (Object.keys(patch).length > 0) {
            this.playingAsset = {
                ...this.playingAsset,
                ...patch,
            } as PlayingAsset;

            if (patch.title) {
                YouboraTracking.getInstance().setOptions({ 'content.title': patch.title });
            }
        }
    };

    public updatePlayingAsset = async (
        asset: PlayingAsset,
        preparedCallback?: () => void,
        forceLoad?: boolean,
        resumeSeekTime?: number | null
    ) => {
        if (this.sameAssetIsPlaying(asset) && !forceLoad) {
            this.syncAssetMetadata(asset);
            return;
        }

        if (this.switchingMode) {
            return;
        }

        this.seekTime = resumeSeekTime != null ? resumeSeekTime : null;
        this.timeShiftStarted = false;
        this.pauseOnLive = false;
        this.pauseLiveTime = null;
        this.pauseLiveTimePlayed = null;
        this.pauseLiveLastPlayTime = null;

        if (resumeSeekTime != null) {
            this.onSeeked(resumeSeekTime, 3);
        }

        const url = new URL(window.location.href);
        const startFromLive = url.searchParams.get('live') === 'true';

        this.playingAsset = asset;

        if (
            isLivePlayerAsset(this.playingAsset) &&
            this.playbackMode === PlayingMode.NORMAL &&
            this.playingAsset.replayContentId &&
            !startFromLive
        ) {
            const bookmark = this.getBookmark();
            if (bookmark > 0) {
                this.switchToTimeShift(bookmark < this.getSeekMax() ? bookmark : 0.1);
                return;
            }
        }

        if (this.assetChangedListener) {
            this.assetChangedListener(this.playingAsset);
        }

        if (this.castConnected) {
            await this.cast(this.playingAsset);
        } else {
            const startTime = this.getStartTime();

            if (this.playerEngine) {
                this.playerEngine.setAsset(
                    this.playingAsset,
                    startTime,
                    !isVodPlayerAsset(this.playingAsset) && !isTrailerPlayerAsset(this.playingAsset) ? 'tv' : 'vod',
                    () => {
                        if (!this.pauseOnLive) {
                            this.log('info', 'play after load');
                            this.setPlayingState(true);
                        }

                        if (preparedCallback) {
                            preparedCallback();
                        }

                        this.switchingMode = false;
                        this.pauseOnLive = false;
                    }
                );
            }
        }
    };

    public getDuration = () => {
        if (!this.playingAsset || !this.playerEngine) {
            return 0;
        }

        if (isLivePlayerAsset(this.playingAsset)) {
            return MILLISECONDS.toSeconds(this.playingAsset.endTime - this.playingAsset.startTime);
        }

        if (
            (isCatchupPlayerAsset(this.playingAsset) || isRecordingPlayerAsset(this.playingAsset)) &&
            isAssetWithinPaddingWindow(this.playingAsset)
        ) {
            const { pre_padding, post_padding } = this.playingAsset;
            const paddingTime = SECONDS.toMillis(post_padding ?? 0) + SECONDS.toMillis(pre_padding ?? 0);
            return MILLISECONDS.toSeconds(this.playingAsset.endTime + paddingTime - this.playingAsset.startTime);
        }

        return this.playerEngine.getDuration() ?? 1;
    };

    public getProgress = () => {
        if (!this.playingAsset || !this.playerEngine) {
            return 0;
        }

        if (this.pauseLiveTime != null) {
            return this.pauseLiveTime;
        }

        if (this.switchingMode && this.seekTime != null) {
            return this.seekTime;
        }

        if (isLivePlayerAsset(this.playingAsset) && this.playbackMode === PlayingMode.NORMAL) {
            return Math.max(0, MILLISECONDS.toSeconds(Date.now() - this.playingAsset.startTime));
        }

        if (isLivePlayerAsset(this.playingAsset) && this.playbackMode === PlayingMode.TIME_SHIFT) {
            if (this.pauseLiveLastPlayTime && this.pauseLiveTimePlayed != null) {
                const diff = Date.now() - this.pauseLiveLastPlayTime;
                return MILLISECONDS.toSeconds(this.pauseLiveTimePlayed + diff);
            }

            if (!this.timeShiftStarted && this.seekTime != null) {
                return this.seekTime;
            }

            if (isSafari()) {
                return this.seekTime ?? this.toPlaybackTime(this.playerEngine.getCurrentTime());
            }

            if (this.castConnected) {
                const ct = this.toPlaybackTime(this.playerEngine.getCurrentTime());
                if (this.seekTime != null && (!ct || ct <= 0 || Math.abs(ct - this.seekTime) > this.LIVE_SEEKMAX_THRESHOLD)) {
                    return this.seekTime;
                }

                return ct;
            }

            const currentTime = this.toPlaybackTime(this.playerEngine.getCurrentTime());
            if (!currentTime || currentTime <= 0) {
                return this.seekTime ?? 0;
            }

            return currentTime;
        }

        const currentTime = this.playerEngine.getCurrentTime();
        return this.seekTime ?? (Number.isFinite(currentTime) ? currentTime : 0);
    };

    public getSecondaryProgress = (): number => {
        if (isLivePlayerAsset(this.playingAsset) && this.playbackMode === PlayingMode.TIME_SHIFT) {
            if (isNow(this.playingAsset.startTime, this.playingAsset.endTime)) {
                return MILLISECONDS.toSeconds(Date.now() - this.playingAsset.startTime);
            }
            if (isPast(this.playingAsset.endTime)) {
                return this.getDuration();
            }
            return 0;
        }

        if (isCatchupPlayerAsset(this.playingAsset) && this.playingAsset?.type === 'LIVE') {
            return MILLISECONDS.toSeconds(Date.now() - this.playingAsset.startTime);
        }

        return this.getProgress();
    };

    public getSeekMax = () => {
        if (isLivePlayerAsset(this.playingAsset)) {
            // return elapsed time if the asset is still live
            if (isNow(this.playingAsset.startTime, this.playingAsset.endTime)) {
                return MILLISECONDS.toSeconds(Date.now() - this.playingAsset.startTime);
            }
            if (isPast(this.playingAsset.endTime)) {
                return this.getDuration();
            }
            return 0;
        }

        if (isCatchupPlayerAsset(this.playingAsset) && this.playingAsset?.type === 'LIVE') {
            return MILLISECONDS.toSeconds(Date.now() - this.playingAsset.startTime);
        }

        return this.getDuration();
    };

    public getThumbnail = async (time: number) => {
        if (!this.playerEngine || this.castConnected) {
            return null;
        }
        const imageTrack = this.playerEngine.getThumbnailTrack();

        if (!imageTrack) {
            return null;
        }
        const { id } = imageTrack;
        const thumb = await this.playerEngine.getThumbnails(id, time);

        if (!thumb) {
            return null;
        }

        return thumb;
    };

    private pauseLiveStream = () => {
        if (!this.playerEngine) {
            return;
        }

        const progress = this.getProgress();

        this.pauseLiveTime = progress;
        this.pauseOnLive = true;
        this.setPlaybackMode(PlayingMode.TIME_SHIFT);

        this.log('info', `pause live stream at ${progress}`);

        this.playerEngine.pause();
    };

    private playPausedLiveStream = async () => {
        this.log('info', `play  paused live stream from ${this.pauseLiveTime}`);

        this.pauseOnLive = false;
        this.pauseLiveTime = null;
        this.setPlaybackMode(PlayingMode.NORMAL);
    };

    public setPlayingState = async (playing: boolean, timeShiftPlayed = false) => {
        this.isPlaying = playing;

        if (this.isPlaying) {
            if (this.sessionExpired) {
                await this.restartExpiredSession();
                return null;
            }

            if (isLivePlayerAsset(this.playingAsset)) {
                // NOTE: first pause works, but the second one advances the paused time... why? :D
                this.pauseLiveLastPlayTime = Date.now();
            }
            if (isLivePlayerAsset(this.playingAsset) && this.pauseLiveTime && !timeShiftPlayed) {
                await this.playPausedLiveStream();
            }
            if (this.playerEngine) {
                this.playerEngine.play();
            }
        } else {
            if (isLivePlayerAsset(this.playingAsset)) {
                this.pauseLiveTimePlayed = Math.max(0, Date.now() - this.playingAsset.startTime);
            }
            if (isLivePlayerAsset(this.playingAsset) && this.playbackMode !== PlayingMode.TIME_SHIFT) {
                // && this.isReplayCapable
                return this.pauseLiveStream();
            }

            if (this.playerEngine) {
                this.playerEngine.pause();
            }
        }

        return null;
    };

    public seek = async (to: number) => {
        this.pauseLiveTime = null;
        if (this.switchingMode) {
            if (this.switchTargetSeek != null && Math.abs(to - this.switchTargetSeek) <= this.LIVE_SEEKMAX_THRESHOLD) {
                return;
            }
            if (this.seekTime != null) {
                this.seekTime = to;
            }
            this.pendingSeek = to;
            return;
        }
        if (!this.playerEngine) {
            return;
        }

        this.log('info', 'seek', to, this.playingAsset.type, this.playbackMode);

        this.timeUpdateReceived = false;

        const seekMax = this.getSeekMax();
        const isLiveSeekMax = seekMax > 0 && seekMax - to <= this.LIVE_SEEKMAX_THRESHOLD;

        if (this.playbackMode === PlayingMode.TIME_SHIFT && isLiveSeekMax) {
            await this.switchToNormalMode();
            return;
        }

        if (!isLivePlayerAsset(this.playingAsset)) {
            this.onSeeked(to, this.castConnected ? 4 : 2);
            this.playerEngine.seekTo(to);
        } else {
            if (this.playbackMode === PlayingMode.NORMAL && isLiveSeekMax) {
                return;
            }

            if (this.pauseOnLive) {
                this.pauseLiveTime = to;
                return;
            }

            await this.seekLiveHandler(to, this.playbackMode);
        }
    };

    public switchToTimeShift = async (from: number) => {
        const asset = this.playingAsset as LivePlayerAssetInfo;

        if (from < 0) {
            return;
        }

        this.beginModeSwitch();
        this.switchTargetSeek = from;
        this.timeShiftStarted = false;
        this.pauseLiveTime = null;
        this.pauseLiveTimePlayed = null;
        this.pauseLiveLastPlayTime = null;
        this.setPlaybackMode(PlayingMode.TIME_SHIFT);
        this.onSeeked(from, this.castConnected ? 5 : 3);

        const replayId = asset?.listingId || asset?.replayContentId;

        await this.switchSession(PlayingMode.TIME_SHIFT, replayId, from);
    };

    public switchToNormalMode = async () => {
        this.log('info', 'switchToNormalMode');

        this.beginModeSwitch();
        this.pendingSeek = null;
        if (isLivePlayerAsset(this.playingAsset) || (this.playingAsset as LivePlayerAssetInfo)?.type === PlayingAssetType.LIVE) {
            (this.playingAsset as LivePlayerAssetInfo).use_case = PlayingAssetType.LIVE;
        }
        if (this.castConnected) {
            CastHandler.resetCastProgress();
        }
        this.setPlaybackMode(PlayingMode.NORMAL);

        const channelId = (this.playingAsset as LivePlayerAssetInfo).channel_id || (this.playingAsset as LivePlayerAssetInfo).channelId;

        if (!channelId) {
            this.log('error', 'No channel_id found for switching to live mode');
            this.endModeSwitch();
            return;
        }

        await this.switchSession(PlayingMode.NORMAL, channelId);
        if (this.castConnected) {
            await this.awaitCastModeReady(PlayingMode.NORMAL);
            this.switchingMode = false;
            this.switchTargetSeek = null;
            this.timeShiftStarted = false;
            this.seekTime = null;
            this.pendingSeek = null;
            CastHandler.resetCastProgress();
        }
    };

    public setCastConnected = async (connected: boolean, currentAsset?: PlayingAsset) => {
        this.castConnected = connected;

        if (this.castConnected) {
            this.stopHeartbeat();
        }

        if (
            currentAsset &&
            isLivePlayerAsset(currentAsset) &&
            isLivePlayerAsset(this.playingAsset) &&
            currentAsset.channel_id === this.playingAsset.channel_id
        ) {
            this.playingAsset = {
                ...this.playingAsset,
                replayUntil: currentAsset.replayUntil,
                replayContentId: currentAsset.replayContentId,
                recordingUntil: currentAsset.recordingUntil,
            };
        }

        if (currentAsset && this.playingAsset) {
            this.playingAsset = {
                ...this.playingAsset,
                image: currentAsset.image ?? this.playingAsset.image,
                title: currentAsset.title ?? this.playingAsset.title,
            };
        }

        if (this.playingAsset) {
            this.log('info', 'with asset');

            if (this.castConnected) {
                await this.endPlaySession(true);
                await this.cast(this.playingAsset);
            } else {
                await this.endPlaySession(true);

                if (this.isLiveChannelAsset() && this.playbackMode === PlayingMode.TIME_SHIFT) {
                    const resumePosition = CastHandler.consumeLastKnownProgress();

                    if (resumePosition != null && resumePosition > 0 && resumePosition < this.getSeekMax()) {
                        (this.playingAsset as LivePlayerAssetInfo).use_case = PlayingAssetType.LIVE;
                        await this.switchToTimeShift(resumePosition);
                        this.unregisterListeners();
                        this.registerListeners();
                        return;
                    }

                    this.seekTime = null;
                    this.timeShiftStarted = false;
                    this.setPlaybackMode(PlayingMode.NORMAL);
                }

                const createSessionResponse = await this.createPlaySession(this.playbackMode);

                if (createSessionResponse?.response) {
                    const response = createSessionResponse.response as {
                        id: string;
                        keepAliveInterval: number;
                        manifestUrl: string;
                        sessionId: string;
                    };

                    this.playSession = response.id;

                    this.playSession = response.sessionId;

                    await this.updatePlayingAsset(
                        {
                            ...this.playingAsset,
                            keepAliveInterval: response.keepAliveInterval,
                            manifestUrl: response.manifestUrl,
                            ...(this.isLiveChannelAsset() ? { use_case: PlayingAssetType.LIVE } : {}),
                        },
                        undefined,
                        true
                    );
                } else {
                    await this.onSessionRejected(this.playbackMode, createSessionResponse?.error);
                }
            }
        }

        this.unregisterListeners();
        this.registerListeners();
    };

    public setPlayingMode = (mode: PlayingMode = PlayingMode.NORMAL) => {
        this.log('info', 'setPlayingMode', mode);
        this.playbackMode = mode;
    };

    public setPlaybackModeChangeListener = (listener: (mode: PlayingMode) => void) => {
        this.playbackModeChangeListener = listener;
    };

    public setAssetChangedListener = (listener: (asset: PlayingAsset) => void) => {
        this.assetChangedListener = listener;
    };

    public setCastInitialisedListener = (listener: () => void) => {
        this.castInitialisedListener = listener;
    };

    public setTakeoverListener = (listener: (state: boolean) => void) => {
        this.takeoverListener = listener;
    };

    public setSessionRejectedListener = (listener: () => void) => {
        this.sessionRejectedListener = listener;
    };

    public isPlayerLoaded = () => this.playingAsset && this.playerEngine;

    public getPlayingAsset = () => this.playingAsset;

    public getPlaybackMode = () => this.playbackMode;

    public setIsReplayCapable = (isReplayCapable: boolean) => {
        this.isReplayCapable = isReplayCapable;
    };

    public destroy = async () => {
        this.log('info', 'destroy');

        if (this.castConnected) {
            this.unregisterListeners();

            if (this.playerEngine) {
                this.playerEngine.resetPlayer(true);
            }

            this.playerEngine = null;

            this.stopHeartbeat();

            if (this.releaseSeekLockTimeout) {
                clearTimeout(this.releaseSeekLockTimeout);
                this.releaseSeekLockTimeout = null;
            }

            if (this.verifySeekTimeout) {
                clearTimeout(this.verifySeekTimeout);
                this.verifySeekTimeout = null;
            }

            return;
        }

        await this.endPlaySession();
        this.unregisterListeners();

        if (this.playerEngine) {
            this.playerEngine.resetPlayer(true);
        }

        this.playerEngine = null;
        this.playingAsset = null;
        this.switchingMode = false;
        this.switchTargetSeek = null;
        this.pendingSeek = null;
        this.pauseLiveTime = null;
        this.pauseOnLive = false;
        this.isReplayCapable = false;

        this.setPlaybackMode(PlayingMode.NORMAL);

        // this.playbackModeChangeListener = null;
        // this.assetChangedListener = null;

        this.stopHeartbeat();

        guardedLocalStorage.removeItem(STREAM_SESSION_ID);

        if (this.releaseSeekLockTimeout) {
            clearTimeout(this.releaseSeekLockTimeout);
            this.releaseSeekLockTimeout = null;
        }

        if (this.verifySeekTimeout) {
            clearTimeout(this.verifySeekTimeout);
            this.verifySeekTimeout = null;
        }
    };
}

export default new PlaybackManager();
